import React, { useEffect, useCallback } from 'react';
import {
  Link,
  Redirect,
  Route,
  Switch,
  useLocation,
  useParams,
} from 'react-router-dom';

import { t } from '@lingui/macro';
import { CaretLeftIcon } from '@patternfly/react-icons';
import { Card, PageSection } from '@patternfly/react-core';

import useRequest from 'hooks/useRequest';
import { InstanceGroupsAPI, SettingsAPI } from 'api';
import RoutedTabs from 'components/RoutedTabs';
import ContentError from 'components/ContentError';
import ContentLoading from 'components/ContentLoading';
import JobList from 'components/JobList';

import ContainerGroupDetails from './ContainerGroupDetails';
import ContainerGroupEdit from './ContainerGroupEdit';

function ContainerGroup({ setBreadcrumb }) {
  const { id } = useParams();
  const { pathname } = useLocation();

  const {
    isLoading,
    error: contentError,
    request: fetchInstanceGroups,
    result: { instanceGroup, defaultControlPlane, defaultExecution },
  } = useRequest(
    useCallback(async () => {
      const [
        { data },
        {
          data: {
            DEFAULT_EXECUTION_QUEUE_NAME,
            DEFAULT_CONTROL_PLANE_QUEUE_NAME,
          },
        },
      ] = await Promise.all([
        InstanceGroupsAPI.readDetail(id),
        SettingsAPI.readAll(),
      ]);
      return {
        instanceGroup: data,
        defaultControlPlane: DEFAULT_CONTROL_PLANE_QUEUE_NAME,
        defaultExecution: DEFAULT_EXECUTION_QUEUE_NAME,
      };
    }, [id]),
    { instanceGroup: null, defaultExecution: '' }
  );

  useEffect(() => {
    fetchInstanceGroups();
  }, [fetchInstanceGroups, pathname]);

  useEffect(() => {
    if (instanceGroup) {
      setBreadcrumb(instanceGroup);
    }
  }, [instanceGroup, setBreadcrumb]);

  const tabsArray = [
    {
      name: (
        <>
          <CaretLeftIcon />
          {t`Back to instance groups`}
        </>
      ),
      link: '/instance_groups',
      id: 99,
    },
    {
      name: t`Details`,
      link: `/instance_groups/container_group/${id}/details`,
      id: 0,
    },
    {
      name: t`Jobs`,
      link: `/instance_groups/container_group/${id}/jobs`,
      id: 1,
    },
  ];

  if (!isLoading && contentError) {
    return (
      <PageSection>
        <Card>
          <ContentError error={contentError}>
            {contentError.response?.status === 404 && (
              <span>
                {t`Container group not found.`}

                <Link to="/instance_groups">{t`View all instance groups`}</Link>
              </span>
            )}
          </ContentError>
        </Card>
      </PageSection>
    );
  }

  let cardHeader = <RoutedTabs tabsArray={tabsArray} />;
  if (pathname.endsWith('edit')) {
    cardHeader = null;
  }

  return (
    <PageSection>
      <Card>
        {cardHeader}
        {isLoading && <ContentLoading />}
        {!isLoading && instanceGroup && (
          <Switch>
            <Redirect
              from="/instance_groups/container_group/:id"
              to="/instance_groups/container_group/:id/details"
              exact
            />
            {instanceGroup && (
              <>
                <Route path="/instance_groups/container_group/:id/edit">
                  <ContainerGroupEdit
                    instanceGroup={instanceGroup}
                    defaultControlPlane={defaultControlPlane}
                    defaultExecution={defaultExecution}
                  />
                </Route>
                <Route path="/instance_groups/container_group/:id/details">
                  <ContainerGroupDetails
                    instanceGroup={instanceGroup}
                    defaultExecution={defaultExecution}
                  />
                </Route>
                <Route path="/instance_groups/container_group/:id/jobs">
                  <JobList
                    showTypeColumn
                    defaultParams={{ instance_group: instanceGroup.id }}
                  />
                </Route>
              </>
            )}
          </Switch>
        )}
      </Card>
    </PageSection>
  );
}

export default ContainerGroup;
